/**
 * Booking pause — a per-activity switch that takes something off sale without
 * unpublishing it.
 *
 * Distinct from stop sales, which blocks specific *dates* for an activity. A
 * pause is about the activity itself being out of action (the jet ski is in
 * for repair), so the page stays live — keeping its search ranking and letting
 * people read it — but the booking control is disabled and explains why.
 *
 * `bookingPaused` is always the master control. `bookingResumesOn` is an
 * optional convenience so "not available this month" doesn't rely on anyone
 * remembering to switch it back on; a resume date on its own never pauses
 * anything.
 */

export interface BookingPauseFields {
  bookingPaused?: boolean | null;
  /** Inclusive: the activity is on sale again on this date. */
  bookingResumesOn?: string | null;
  /** Customer-facing reason, shown next to the disabled booking control. */
  bookingPausedNote?: string | null;
}

/** `YYYY-MM-DD`, ignoring any time part so this stays a question about days. */
function calendarDay(value: string): string | null {
  const day = value.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(day) ? day : null;
}

/**
 * Whether bookings are currently closed for this activity.
 *
 * `today` is a plain `YYYY-MM-DD` (a full ISO timestamp is accepted and
 * truncated). Dates are compared as strings — `YYYY-MM-DD` sorts
 * chronologically, so this never drags timezones into a calendar question.
 */
export function isBookingPaused(
  activity: BookingPauseFields | null | undefined,
  today: string,
): boolean {
  if (!activity?.bookingPaused) return false;

  const resumesOn = activity.bookingResumesOn
    ? calendarDay(activity.bookingResumesOn)
    : null;
  if (!resumesOn) return true;

  const now = calendarDay(today);
  if (!now) return true;

  return now < resumesOn;
}
